// Search-as-you-type (spec/dashboard.md section 2.3 DataGrid, section 6.1 the grid, section 6.6 search, section 7.2 card
// pages): every search box applies the typed text after a pause of SEARCH_DEBOUNCE_MS, Enter applies it at once, and a
// text equal to the last applied one (trimmed) never applies again, so no request repeats for the same text.
import { useCallback, useEffect, useRef, useState } from 'react';

/** The pause after the last keystroke before a search box applies its text. */
export const SEARCH_DEBOUNCE_MS = 300;

/** `value` once it has stayed the same for `delay` ms. */
export function useDebouncedValue<T>(value: T, delay = SEARCH_DEBOUNCE_MS): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

export interface SearchBox {
  /** the typed text (the input's value) */
  text: string;
  setText: (text: string) => void;
  /** apply the typed text now (Enter) */
  applyNow: () => void;
}

/**
 * A search box over an applied value (component state or the URL): the typed text is applied through `onApply` after
 * the pause or at once by `applyNow`; it is trimmed, and never applied when equal to the last applied text. An outside
 * change of `applied` (a link, Back, a reset) replaces the typed text.
 */
export function useSearchBox(applied: string, onApply: (text: string) => void, delay = SEARCH_DEBOUNCE_MS): SearchBox {
  const [text, setText] = useState(applied);
  const last = useRef(applied);
  const onApplyRef = useRef(onApply);
  useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);
  const apply = useCallback((typed: string) => {
    const next = typed.trim();
    if (next === last.current.trim()) return;
    last.current = next;
    onApplyRef.current(next);
  }, []);
  const settled = useDebouncedValue(text, delay);
  useEffect(() => {
    apply(settled);
  }, [settled, apply]);
  useEffect(() => {
    if (applied !== last.current) {
      last.current = applied;
      setText(applied);
    }
  }, [applied]);
  const applyNow = useCallback(() => apply(text), [apply, text]);
  return { text, setText, applyNow };
}
